/**
 * FikoThicknessShader
 *
 * Profesyonel CT tarama kalitesinde wall thickness görselleştirme
 * Screen-space post-processing ile SMOOTH sınırlar - üçgensel görünüm YOK
 *
 * Teknik: 2-pass rendering (FikoLiquidFillShader benzeri)
 * 1. Pass: Thickness data render (R=normalized thickness, G=sssFactor, B=normal.x, A=normal.y)
 * 2. Pass: Bilateral blur + color mapping + SSS + kritik pulse + lighting
 *
 * Bu Moldflow/CATIA/SolidWorks'ün kullandığı teknik.
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

/**
 * Kalınlık görselleştirme ayarları
 */
export interface ThicknessVisualizationConfig {
    /** Minimum kalınlık (mm) - renk skalası başlangıcı */
    minThickness: number;

    /** Maksimum kalınlık (mm) - renk skalası sonu */
    maxThickness: number;

    /** Kritik kalınlık eşiği (mm) */
    criticalThickness: number;

    /** Kritik bölge vurgulama */
    highlightCritical: boolean;

    /** Kritik pulse animasyon hızı */
    criticalPulseSpeed: number;

    /** Kritik pulse yoğunluğu (0-1) */
    criticalPulseIntensity: number;

    /** Bilateral blur radius */
    blurRadius: number;

    /** Blur sigma (edge sensitivity) */
    blurSigma: number;

    /** SSS (Subsurface Scattering) etkinliği */
    sssEnabled: boolean;

    /** SSS yoğunluğu */
    sssIntensity: number;

    /** SSS rengi */
    sssColor: THREE.Color;

    /** Ambient Occlusion yoğunluğu */
    aoIntensity: number;

    /** Edge glow (kenar parlaması) */
    edgeGlow: boolean;

    /** Edge glow rengi */
    edgeGlowColor: THREE.Color;

    /** Renk skalası preset */
    colorPreset: 'ct_scan' | 'thermal' | 'rainbow' | 'industrial' | 'scientific';
}

const DEFAULT_CONFIG: ThicknessVisualizationConfig = {
    minThickness: 0.5,
    maxThickness: 20.0,
    criticalThickness: 1.0,
    highlightCritical: true,
    criticalPulseSpeed: 3.0,
    criticalPulseIntensity: 0.6,
    blurRadius: 6,
    blurSigma: 2.5,
    sssEnabled: true,
    sssIntensity: 0.3,
    sssColor: new THREE.Color(0xff6644),
    aoIntensity: 2.0,
    edgeGlow: true,
    edgeGlowColor: new THREE.Color(0x00ffff),
    colorPreset: 'ct_scan'
};

// ============================================
// PASS 1: Thickness Data Shader
// Outputs: R = normalized thickness, G = SSS factor, B/A = encoded normal
// ============================================

const thicknessDataVertexShader = /* glsl */`
    attribute float thickness;
    attribute float sssFactor;

    varying float vThickness;
    varying float vSSS;
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    varying vec3 vWorldPosition;

    void main() {
        vThickness = thickness;
        vSSS = sssFactor;
        vNormal = normalize(normalMatrix * normal);
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = -mvPosition.xyz;

        gl_Position = projectionMatrix * mvPosition;
    }
`;

const thicknessDataFragmentShader = /* glsl */`
    precision highp float;

    uniform float uMinThickness;
    uniform float uMaxThickness;
    uniform float uCriticalThickness;

    varying float vThickness;
    varying float vSSS;
    varying vec3 vNormal;
    varying vec3 vViewPosition;

    void main() {
        // Normalize thickness to 0-1 range
        float range = uMaxThickness - uMinThickness;
        float normalizedThickness = clamp((vThickness - uMinThickness) / range, 0.0, 1.0);

        // Geçersiz kalınlık kontrolü
        if (vThickness <= 0.0 || vThickness >= uMaxThickness * 0.99) {
            normalizedThickness = 1.0; // Maksimum renk
        }

        // Kritik bölge bayrağı
        float isCritical = (vThickness < uCriticalThickness && vThickness > 0.0) ? 1.0 : 0.0;

        // Normal'ı encode et (-1,1) -> (0,1)
        vec3 n = normalize(vNormal);

        // Output:
        // R = normalized thickness
        // G = SSS factor + critical flag (0.5 eklenerek)
        // B = encoded normal X
        // A = encoded normal Y
        gl_FragColor = vec4(
            normalizedThickness,
            vSSS + isCritical * 0.5,
            n.x * 0.5 + 0.5,
            n.y * 0.5 + 0.5
        );
    }
`;

// ============================================
// PASS 2: Blur + Color Mapping + Composite Shader
// Professional CT-scan quality smooth gradients
// ============================================

const compositeVertexShader = /* glsl */`
    varying vec2 vUv;

    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const compositeFragmentShader = /* glsl */`
    precision highp float;

    uniform sampler2D tData;
    uniform sampler2D tDepth;
    uniform vec2 uResolution;
    uniform float uBlurRadius;
    uniform float uBlurSigma;
    uniform float uTime;

    // Kalınlık parametreleri
    uniform float uMinThickness;
    uniform float uMaxThickness;
    uniform float uCriticalThickness;

    // Kritik vurgulama
    uniform bool uHighlightCritical;
    uniform float uCriticalPulseSpeed;
    uniform float uCriticalPulseIntensity;

    // SSS
    uniform bool uSSSEnabled;
    uniform float uSSSIntensity;
    uniform vec3 uSSSColor;

    // Lighting & AO
    uniform float uAOIntensity;
    uniform vec3 uLightDir;

    // Edge glow
    uniform bool uEdgeGlow;
    uniform vec3 uEdgeGlowColor;

    // Renk skalası (8 nokta)
    uniform float uColorThicknesses[8];
    uniform vec3 uColorValues[8];

    varying vec2 vUv;

    // Gaussian weight
    float gaussian(float x, float sigma) {
        return exp(-(x * x) / (2.0 * sigma * sigma));
    }

    // Bilateral blur - edge-preserving smooth
    vec4 bilateralBlur(sampler2D tex, vec2 uv, vec2 resolution, float radius, float sigma) {
        vec4 center = texture2D(tex, uv);
        float centerThickness = center.r;

        vec4 result = vec4(0.0);
        float totalWeight = 0.0;

        float sigmaSpatial = sigma;
        float sigmaRange = 0.08; // Thickness difference sensitivity

        for (int x = -8; x <= 8; x++) {
            for (int y = -8; y <= 8; y++) {
                float fx = float(x);
                float fy = float(y);
                if (abs(fx) > radius || abs(fy) > radius) continue;

                vec2 offset = vec2(fx, fy) / resolution;
                vec4 sampleVal = texture2D(tex, uv + offset);

                // Spatial weight
                float spatialDist = length(vec2(fx, fy));
                float spatialWeight = gaussian(spatialDist, sigmaSpatial);

                // Range weight (similarity in thickness)
                float thicknessDiff = abs(sampleVal.r - centerThickness);
                float rangeWeight = gaussian(thicknessDiff, sigmaRange);

                float weight = spatialWeight * rangeWeight;

                result += sampleVal * weight;
                totalWeight += weight;
            }
        }

        return result / max(totalWeight, 0.001);
    }

    // Smootherstep - 6th order polynomial for ultra-smooth transitions
    float smootherstep(float edge0, float edge1, float x) {
        x = clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);
        return x * x * x * (x * (x * 6.0 - 15.0) + 10.0);
    }

    // CT-scan quality color gradient
    vec3 getGradientColor(float normalizedThickness) {
        float thickness = mix(uMinThickness, uMaxThickness, normalizedThickness);

        // İlk renk (en ince)
        if (thickness <= uColorThicknesses[0]) {
            return uColorValues[0];
        }

        // Son renk (en kalın)
        if (thickness >= uColorThicknesses[7]) {
            return uColorValues[7];
        }

        // Aradaki smooth interpolasyon
        for (int i = 0; i < 7; i++) {
            float t1 = uColorThicknesses[i];
            float t2 = uColorThicknesses[i + 1];

            if (thickness >= t1 && thickness <= t2) {
                // Smootherstep ile ultra-yumuşak geçiş
                float t = smootherstep(t1, t2, thickness);
                return mix(uColorValues[i], uColorValues[i + 1], t);
            }
        }

        return uColorValues[7];
    }

    // Edge detection (Sobel) for glow effect
    float detectEdge(vec2 uv, vec2 resolution) {
        vec2 texel = 1.0 / resolution;

        float tl = texture2D(tData, uv + vec2(-texel.x, texel.y)).r;
        float t  = texture2D(tData, uv + vec2(0.0, texel.y)).r;
        float tr = texture2D(tData, uv + vec2(texel.x, texel.y)).r;
        float l  = texture2D(tData, uv + vec2(-texel.x, 0.0)).r;
        float r  = texture2D(tData, uv + vec2(texel.x, 0.0)).r;
        float bl = texture2D(tData, uv + vec2(-texel.x, -texel.y)).r;
        float b  = texture2D(tData, uv + vec2(0.0, -texel.y)).r;
        float br = texture2D(tData, uv + vec2(texel.x, -texel.y)).r;

        float gx = -tl - 2.0*l - bl + tr + 2.0*r + br;
        float gy = -tl - 2.0*t - tr + bl + 2.0*b + br;

        return sqrt(gx*gx + gy*gy);
    }

    void main() {
        // Bilateral blur ile smooth data al
        vec4 data = bilateralBlur(tData, vUv, uResolution, uBlurRadius, uBlurSigma);

        float normalizedThickness = data.r;
        float sssFactor = fract(data.g * 2.0); // Lower bits
        float isCritical = data.g > 0.5 ? 1.0 : 0.0; // Upper bit

        // Normal decode
        float nx = data.b * 2.0 - 1.0;
        float ny = data.a * 2.0 - 1.0;
        float nz = sqrt(max(0.0, 1.0 - nx*nx - ny*ny));
        vec3 normal = normalize(vec3(nx, ny, nz));

        // Gerçek kalınlık değeri (mm)
        float thickness = mix(uMinThickness, uMaxThickness, normalizedThickness);

        // ========== RENK HESAPLAMA ==========
        vec3 baseColor = getGradientColor(normalizedThickness);

        // ========== LIGHTING ==========
        vec3 lightDir = normalize(uLightDir);
        float NdotL = max(dot(normal, lightDir), 0.0);
        float ambient = 0.35;
        float diffuse = NdotL * 0.65;

        // ========== AMBIENT OCCLUSION ==========
        // Screen-space curvature-based AO
        float aoSample1 = texture2D(tData, vUv + vec2(0.004, 0.0)).r;
        float aoSample2 = texture2D(tData, vUv - vec2(0.004, 0.0)).r;
        float aoSample3 = texture2D(tData, vUv + vec2(0.0, 0.004)).r;
        float aoSample4 = texture2D(tData, vUv - vec2(0.0, 0.004)).r;

        float curvature = abs(aoSample1 - 2.0 * normalizedThickness + aoSample2) +
                         abs(aoSample3 - 2.0 * normalizedThickness + aoSample4);
        float ao = 1.0 - clamp(curvature * uAOIntensity, 0.0, 0.5);

        // ========== SSS (Subsurface Scattering) ==========
        vec3 sssContribution = vec3(0.0);
        if (uSSSEnabled && sssFactor > 0.01) {
            // İnce kısımlarda daha fazla ışık geçirgenliği
            float sssStrength = (1.0 - normalizedThickness) * sssFactor * uSSSIntensity;

            // Rim lighting benzeri efekt
            float rimFactor = 1.0 - max(dot(normal, vec3(0.0, 0.0, 1.0)), 0.0);
            rimFactor = pow(rimFactor, 2.0);

            sssContribution = uSSSColor * sssStrength * (0.5 + 0.5 * rimFactor);
        }

        // ========== KRİTİK BÖLGE VURGULAMA ==========
        vec3 criticalContribution = vec3(0.0);
        if (uHighlightCritical && isCritical > 0.5) {
            // Animasyonlu pulse
            float pulse = 0.5 + 0.5 * sin(uTime * uCriticalPulseSpeed);

            // Kritik eşiğe yakınlık oranı
            float criticalRatio = 1.0 - (thickness / uCriticalThickness);
            criticalRatio = clamp(criticalRatio, 0.0, 1.0);

            // Kırmızı vurgulama
            criticalContribution = vec3(1.0, 0.0, 0.0) * criticalRatio * pulse * uCriticalPulseIntensity;
        }

        // ========== EDGE GLOW ==========
        vec3 edgeContribution = vec3(0.0);
        if (uEdgeGlow) {
            float edge = detectEdge(vUv, uResolution);

            // Kalınlık değişim sınırlarında glow
            if (edge > 0.03) {
                float edgeGlow = smootherstep(0.03, 0.2, edge);
                edgeContribution = uEdgeGlowColor * edgeGlow * 0.15;
            }
        }

        // ========== FİNAL COMPOSITE ==========
        float lighting = ambient + diffuse;
        vec3 finalColor = baseColor * lighting * ao;

        // SSS ekleme
        finalColor += sssContribution;

        // Kritik vurgulama
        finalColor += criticalContribution;

        // Edge glow
        finalColor += edgeContribution;

        // Tone mapping (HDR to LDR)
        finalColor = finalColor / (finalColor + vec3(1.0));

        // Gamma correction
        finalColor = pow(finalColor, vec3(1.0 / 2.2));

        gl_FragColor = vec4(finalColor, 1.0);
    }
`;

/**
 * Kalınlık data material (Pass 1 - sadece data output)
 */
export class FikoThicknessDataMaterial extends THREE.ShaderMaterial {
    constructor(config: Partial<ThicknessVisualizationConfig> = {}) {
        const cfg = { ...DEFAULT_CONFIG, ...config };

        super({
            vertexShader: thicknessDataVertexShader,
            fragmentShader: thicknessDataFragmentShader,
            uniforms: {
                uMinThickness: { value: cfg.minThickness },
                uMaxThickness: { value: cfg.maxThickness },
                uCriticalThickness: { value: cfg.criticalThickness }
            },
            side: THREE.DoubleSide
        });
    }

    update(config?: Partial<ThicknessVisualizationConfig>): void {
        if (config) {
            if (config.minThickness !== undefined) this.uniforms.uMinThickness.value = config.minThickness;
            if (config.maxThickness !== undefined) this.uniforms.uMaxThickness.value = config.maxThickness;
            if (config.criticalThickness !== undefined) this.uniforms.uCriticalThickness.value = config.criticalThickness;
        }
    }
}

// ============================================
// Doğrudan Renk Görselleştirme Material
// Post-processing olmadan direkt renk çıkışı (fallback)
// ============================================

const directColorVertexShader = /* glsl */`
    attribute float thickness;
    attribute float sssFactor;

    varying float vThickness;
    varying float vSSS;
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    varying vec3 vWorldPosition;

    void main() {
        vThickness = thickness;
        vSSS = sssFactor;
        vNormal = normalize(normalMatrix * normal);

        // World position - fragment shader'da smooth sampling için
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = -mvPosition.xyz;

        gl_Position = projectionMatrix * mvPosition;
    }
`;

const directColorFragmentShader = /* glsl */`
    precision highp float;

    uniform float uMinThickness;
    uniform float uMaxThickness;
    uniform float uCriticalThickness;
    uniform float uTime;

    // Renk skalası (8 nokta)
    uniform float uColorThicknesses[8];
    uniform vec3 uColorValues[8];

    // SSS
    uniform bool uSSSEnabled;
    uniform float uSSSIntensity;
    uniform vec3 uSSSColor;

    // Kritik vurgulama
    uniform bool uHighlightCritical;
    uniform float uCriticalPulseSpeed;
    uniform float uCriticalPulseIntensity;

    varying float vThickness;
    varying float vSSS;
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    varying vec3 vWorldPosition;

    // =============================================
    // FLOW ANALİZİ SCREEN-SPACE AA TEKNİĞİ
    // FikoSmoothFillShader'dan BİREBİR kopyalandı
    // ÜÇGEN KENARLARI TAMAMEN GİZLENİR
    // =============================================

    // Super-smooth sigmoid with adaptive sharpness
    // Screen-space rate'e göre otomatik yumuşatma
    float aaSigmoid(float x, float sharpness) {
        float afwidth = length(vec2(dFdx(x), dFdy(x))) * 3.0;
        float adaptedSharpness = sharpness / (1.0 + afwidth * sharpness * 0.5);
        return 1.0 / (1.0 + exp(-adaptedSharpness * x));
    }

    // Anti-aliased smooth transition
    float aaSmooth(float edge0, float edge1, float value) {
        float afwidth = length(vec2(dFdx(value), dFdy(value))) * 2.0;
        float width = max(edge1 - edge0, afwidth * 2.0);
        return smoothstep(edge0 - afwidth, edge0 + width + afwidth, value);
    }

    // Gaussian falloff with AA
    float aaGaussian(float x, float width) {
        float afwidth = length(vec2(dFdx(x), dFdy(x)));
        float effectiveWidth = max(width, afwidth * 2.0);
        float t = x / effectiveWidth;
        return exp(-t * t * 1.5);
    }

    // =============================================
    // CT-SCAN KALİTESİNDE RENK GRADİENT
    // Flow analizi'nin AYNI tekniği ile SIVISAL görünüm
    // =============================================

    vec3 getThicknessColor() {
        // =============================================
        // SCREEN-SPACE ANTI-ALIASING - FLOW ANALİZİ TEKNİĞİ
        // vThickness (varying) üzerinde dFdx/dFdy ile
        // üçgen kenarlarındaki sıçramayı ölç ve ona göre smooth yap
        // =============================================

        // Screen-space rate - thickness'ın ekranda ne kadar hızlı değiştiği
        // Bu değer üçgen kenarlarında YÜKSEK olur (ani sıçrama)
        // Üçgen içinde DÜŞÜK olur (smooth interpolasyon)
        float screenSpaceRate = length(vec2(dFdx(vThickness), dFdy(vThickness)));

        // Normalize et
        float totalRange = uMaxThickness - uMinThickness;
        float normalizedT = clamp((vThickness - uMinThickness) / totalRange, 0.0, 1.0);

        // Normalized screen-space rate
        float normalizedRate = screenSpaceRate / totalRange;

        // =============================================
        // ADAPTİF YUMUŞATMA - FLOW ANALİZİ YAKLAŞIMI
        // screenSpaceRate yüksekse = üçgen kenarı
        // Bu durumda geçişi ÇOOOOK genişlet
        // =============================================

        // Base band width (normalize aralıkta)
        float baseBandWidth = 0.12;

        // Adaptive band width - screen-space rate'e göre AGRESIF genişlet
        // Flow analizi: max(baseFrontWidth, screenSpaceRate * 8.0)
        // Biz daha da agresif: screenSpaceRate * 15.0
        float adaptiveBandWidth = max(baseBandWidth, normalizedRate * 15.0);

        // 8 renk bandı arasında geçiş
        float bandSize = 1.0 / 7.0;

        // Hangi bandın başlangıcına yakınız?
        float bandPos = normalizedT / bandSize;
        int bandIndex = int(floor(bandPos));
        bandIndex = clamp(bandIndex, 0, 6);

        // Band içindeki pozisyon (0-1)
        float bandT = fract(bandPos);

        // =============================================
        // ULTRA-SMOOTH SIGMOID GEÇİŞİ
        // Düşük sharpness = çok geniş geçiş bölgesi
        // =============================================

        // Band ortasına göre diff hesapla (-0.5 to 0.5)
        float diff = bandT - 0.5;

        // Sharpness - adaptive band width'e göre ayarla
        // Büyük adaptiveBandWidth = düşük sharpness = smooth
        float sharpness = 3.0 / max(adaptiveBandWidth, 0.01);

        // aaSigmoid ile screen-space smooth geçiş
        float smoothT = aaSigmoid(diff, sharpness);

        // İki renk arasında lerp
        vec3 color1 = uColorValues[bandIndex];
        vec3 color2 = uColorValues[min(bandIndex + 1, 7)];

        return mix(color1, color2, smoothT);
    }

    void main() {
        // Geçersiz thickness kontrolü
        if (vThickness <= 0.0 || vThickness >= uMaxThickness * 0.99) {
            gl_FragColor = vec4(0.3, 0.3, 0.3, 1.0);
            return;
        }

        // ========================================
        // RENK HESAPLAMA - SCREEN-SPACE SMOOTHING
        // ========================================

        vec3 baseColor = getThicknessColor();

        // ========================================
        // LIGHTING - PROFESYONEL
        // ========================================

        vec3 lightDir = normalize(vec3(0.4, 0.7, 0.5));
        vec3 normal = normalize(vNormal);
        float NdotL = max(dot(normal, lightDir), 0.0);

        float ambient = 0.45;
        float diffuse = 0.55 * NdotL;

        vec3 litColor = baseColor * (ambient + diffuse);

        // ========================================
        // SSS (Subsurface Scattering) - CT-SCAN EFEKTİ
        // ========================================

        if (uSSSEnabled && vSSS > 0.01) {
            // Screen-space rate for SSS smoothing
            float sssRate = length(vec2(dFdx(vSSS), dFdy(vSSS)));
            float adaptiveSSS = aaSmooth(0.0, max(0.1, sssRate * 5.0), vSSS);

            float backlight = max(dot(normal, -lightDir), 0.0);
            vec3 sss = uSSSColor * backlight * adaptiveSSS * uSSSIntensity;
            litColor += sss;
        }

        // ========================================
        // KRİTİK BÖLGE - SCREEN-SPACE SMOOTH PULSE
        // ========================================

        if (uHighlightCritical && vThickness < uCriticalThickness * 1.5) {
            // Kritik bölgeye yakınlık (0-1)
            float criticalRatio = vThickness / (uCriticalThickness * 1.5);

            // Screen-space rate for critical smoothing
            float criticalRate = length(vec2(dFdx(criticalRatio), dFdy(criticalRatio)));
            float adaptiveCriticalWidth = max(0.15, criticalRate * 8.0);

            // aaSigmoid ile smooth geçiş
            float diff = criticalRatio - 0.5;
            float criticalT = 1.0 - aaSigmoid(diff, 4.0 / adaptiveCriticalWidth);

            // Pulse efekti
            float pulse = 0.5 + 0.5 * sin(uTime * uCriticalPulseSpeed);
            float criticalFactor = criticalT * pulse * uCriticalPulseIntensity;

            // Kırmızı vurgulama - smooth geçiş
            litColor = mix(litColor, vec3(1.0, 0.1, 0.0), criticalFactor);
        }

        // ========================================
        // RIM LIGHTING
        // ========================================

        vec3 viewDir = normalize(-vViewPosition);
        float rim = 1.0 - max(dot(viewDir, normal), 0.0);
        rim = pow(rim, 2.5) * 0.15;
        rim = min(rim, 0.3);
        litColor += vec3(rim);

        // ========================================
        // SPECULAR HIGHLIGHT
        // ========================================

        vec3 halfVec = normalize(lightDir + viewDir);
        float spec = pow(max(dot(normal, halfVec), 0.0), 32.0);
        litColor += vec3(spec * 0.12);

        // Gamma correction
        litColor = pow(litColor, vec3(1.0 / 2.2));
        litColor = clamp(litColor, 0.0, 1.0);

        gl_FragColor = vec4(litColor, 1.0);
    }
`;

/**
 * Doğrudan renk görselleştirme material
 * Post-processing gerekmeden direkt kullanılabilir (fallback veya performans modu)
 */
export class FikoDirectColorMaterial extends THREE.ShaderMaterial {
    private colorPreset: string = 'ct_scan';

    constructor(config: Partial<ThicknessVisualizationConfig> = {}) {
        const cfg = { ...DEFAULT_CONFIG, ...config };
        const preset = ColorPresets[cfg.colorPreset] || ColorPresets.ct_scan;

        // Renk skalası uniform'ları
        const colorThicknesses = new Float32Array(8);
        const colorValues: THREE.Color[] = [];

        for (let i = 0; i < 8; i++) {
            colorThicknesses[i] = preset.thicknesses[i] || 100;
            colorValues.push(preset.colors[i]?.clone() || new THREE.Color(0x808080));
        }

        super({
            vertexShader: directColorVertexShader,
            fragmentShader: directColorFragmentShader,
            uniforms: {
                uMinThickness: { value: cfg.minThickness },
                uMaxThickness: { value: cfg.maxThickness },
                uCriticalThickness: { value: cfg.criticalThickness },
                uTime: { value: 0 },
                uColorThicknesses: { value: colorThicknesses },
                uColorValues: { value: colorValues },
                uSSSEnabled: { value: cfg.sssEnabled },
                uSSSIntensity: { value: cfg.sssIntensity },
                uSSSColor: { value: cfg.sssColor.clone() },
                uHighlightCritical: { value: cfg.highlightCritical },
                uCriticalPulseSpeed: { value: cfg.criticalPulseSpeed },
                uCriticalPulseIntensity: { value: cfg.criticalPulseIntensity }
            },
            side: THREE.DoubleSide
        });

        this.colorPreset = cfg.colorPreset;
    }

    update(time: number): void {
        this.uniforms.uTime.value = time;
    }

    setConfig(config: Partial<ThicknessVisualizationConfig>): void {
        const u = this.uniforms;

        if (config.minThickness !== undefined) u.uMinThickness.value = config.minThickness;
        if (config.maxThickness !== undefined) u.uMaxThickness.value = config.maxThickness;
        if (config.criticalThickness !== undefined) u.uCriticalThickness.value = config.criticalThickness;
        if (config.sssEnabled !== undefined) u.uSSSEnabled.value = config.sssEnabled;
        if (config.sssIntensity !== undefined) u.uSSSIntensity.value = config.sssIntensity;
        if (config.sssColor !== undefined) u.uSSSColor.value.copy(config.sssColor);
        if (config.highlightCritical !== undefined) u.uHighlightCritical.value = config.highlightCritical;
        if (config.criticalPulseSpeed !== undefined) u.uCriticalPulseSpeed.value = config.criticalPulseSpeed;
        if (config.criticalPulseIntensity !== undefined) u.uCriticalPulseIntensity.value = config.criticalPulseIntensity;

        // Renk preset değişikliği
        if (config.colorPreset !== undefined && config.colorPreset !== this.colorPreset) {
            this.colorPreset = config.colorPreset;
            const preset = ColorPresets[config.colorPreset] || ColorPresets.ct_scan;

            const colorThicknesses = new Float32Array(8);
            const colorValues: THREE.Color[] = [];

            for (let i = 0; i < 8; i++) {
                colorThicknesses[i] = preset.thicknesses[i] || 100;
                colorValues.push(preset.colors[i]?.clone() || new THREE.Color(0x808080));
            }

            u.uColorThicknesses.value = colorThicknesses;
            u.uColorValues.value = colorValues;
        }
    }
}

/**
 * Renk presetleri
 */
export const ColorPresets: Record<string, { thicknesses: number[], colors: THREE.Color[] }> = {
    ct_scan: {
        thicknesses: [0.5, 1.0, 2.0, 4.0, 6.0, 10.0, 15.0, 25.0],
        colors: [
            new THREE.Color(0xff0000), // Kırmızı (çok ince)
            new THREE.Color(0xff4400), // Turuncu-kırmızı
            new THREE.Color(0xff8800), // Turuncu
            new THREE.Color(0xffcc00), // Sarı
            new THREE.Color(0xaaff00), // Sarı-yeşil
            new THREE.Color(0x00ff44), // Yeşil
            new THREE.Color(0x00ccff), // Cyan
            new THREE.Color(0x0066ff)  // Mavi (kalın)
        ]
    },
    thermal: {
        thicknesses: [0.5, 2.0, 5.0, 8.0, 12.0, 18.0, 25.0, 35.0],
        colors: [
            new THREE.Color(0x000000), // Siyah
            new THREE.Color(0x4400aa), // Mor
            new THREE.Color(0xff0000), // Kırmızı
            new THREE.Color(0xff6600), // Turuncu
            new THREE.Color(0xffff00), // Sarı
            new THREE.Color(0xffffff), // Beyaz
            new THREE.Color(0xccffff), // Açık cyan
            new THREE.Color(0x88ccff)  // Açık mavi
        ]
    },
    rainbow: {
        thicknesses: [0.5, 1.5, 3.0, 5.0, 8.0, 12.0, 18.0, 25.0],
        colors: [
            new THREE.Color(0xff0000), // Kırmızı
            new THREE.Color(0xff8000), // Turuncu
            new THREE.Color(0xffff00), // Sarı
            new THREE.Color(0x80ff00), // Açık yeşil
            new THREE.Color(0x00ff00), // Yeşil
            new THREE.Color(0x00ffff), // Cyan
            new THREE.Color(0x0080ff), // Açık mavi
            new THREE.Color(0x8000ff)  // Mor
        ]
    },
    industrial: {
        thicknesses: [1.0, 2.0, 3.0, 5.0, 8.0, 12.0, 20.0, 30.0],
        colors: [
            new THREE.Color(0xcc0000), // Koyu kırmızı
            new THREE.Color(0xff3300), // Kırmızı-turuncu
            new THREE.Color(0xff9900), // Turuncu
            new THREE.Color(0xcccc00), // Koyu sarı
            new THREE.Color(0x66aa00), // Yeşil-sarı
            new THREE.Color(0x228822), // Koyu yeşil
            new THREE.Color(0x006666), // Koyu cyan
            new THREE.Color(0x003366)  // Koyu mavi
        ]
    },
    scientific: {
        thicknesses: [0.5, 1.0, 2.0, 3.5, 5.5, 8.5, 13.0, 20.0],
        colors: [
            new THREE.Color(0x440154), // Viridis başlangıç
            new THREE.Color(0x482677),
            new THREE.Color(0x3e4989),
            new THREE.Color(0x31688e),
            new THREE.Color(0x26828e),
            new THREE.Color(0x35b779),
            new THREE.Color(0x6ece58),
            new THREE.Color(0xfde725)  // Viridis bitiş
        ]
    }
};

/**
 * Ana Compositor - Multi-pass rendering yöneticisi
 * FikoLiquidFillCompositor benzeri profesyonel post-processing
 */
export class FikoThicknessCompositor {
    private renderer: THREE.WebGLRenderer;
    private config: ThicknessVisualizationConfig;

    // Render targets
    private dataTarget: THREE.WebGLRenderTarget;

    // Materials
    private compositeMaterial: THREE.ShaderMaterial;

    // Full-screen quad
    private fsQuad: FullScreenQuad;

    // State
    private width: number = 0;
    private height: number = 0;
    private time: number = 0;

    constructor(renderer: THREE.WebGLRenderer, config: Partial<ThicknessVisualizationConfig> = {}) {
        this.renderer = renderer;
        this.config = { ...DEFAULT_CONFIG, ...config };

        const size = renderer.getSize(new THREE.Vector2());
        this.width = size.x;
        this.height = size.y;

        // Data render target (thickness data)
        this.dataTarget = new THREE.WebGLRenderTarget(this.width, this.height, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: THREE.FloatType
        });

        // Composite material - renk skalasını hazırla
        const preset = ColorPresets[this.config.colorPreset] || ColorPresets.ct_scan;
        const colorThicknesses = new Float32Array(8);
        const colorValues: THREE.Color[] = [];

        for (let i = 0; i < 8; i++) {
            colorThicknesses[i] = preset.thicknesses[i] || 100;
            colorValues.push(preset.colors[i] || new THREE.Color(0x808080));
        }

        this.compositeMaterial = new THREE.ShaderMaterial({
            vertexShader: compositeVertexShader,
            fragmentShader: compositeFragmentShader,
            uniforms: {
                tData: { value: this.dataTarget.texture },
                tDepth: { value: null },
                uResolution: { value: new THREE.Vector2(this.width, this.height) },
                uBlurRadius: { value: this.config.blurRadius },
                uBlurSigma: { value: this.config.blurSigma },
                uTime: { value: 0 },

                uMinThickness: { value: this.config.minThickness },
                uMaxThickness: { value: this.config.maxThickness },
                uCriticalThickness: { value: this.config.criticalThickness },

                uHighlightCritical: { value: this.config.highlightCritical },
                uCriticalPulseSpeed: { value: this.config.criticalPulseSpeed },
                uCriticalPulseIntensity: { value: this.config.criticalPulseIntensity },

                uSSSEnabled: { value: this.config.sssEnabled },
                uSSSIntensity: { value: this.config.sssIntensity },
                uSSSColor: { value: this.config.sssColor },

                uAOIntensity: { value: this.config.aoIntensity },
                uLightDir: { value: new THREE.Vector3(0.5, 0.8, 0.3).normalize() },

                uEdgeGlow: { value: this.config.edgeGlow },
                uEdgeGlowColor: { value: this.config.edgeGlowColor },

                uColorThicknesses: { value: colorThicknesses },
                uColorValues: { value: colorValues }
            }
        });

        this.fsQuad = new FullScreenQuad(this.compositeMaterial);
    }

    /**
     * Boyut değişikliği
     */
    setSize(width: number, height: number): void {
        this.width = width;
        this.height = height;

        this.dataTarget.setSize(width, height);
        this.compositeMaterial.uniforms.uResolution.value.set(width, height);
    }

    /**
     * Konfigürasyon güncelleme
     */
    setConfig(config: Partial<ThicknessVisualizationConfig>): void {
        Object.assign(this.config, config);

        const u = this.compositeMaterial.uniforms;

        if (config.blurRadius !== undefined) u.uBlurRadius.value = config.blurRadius;
        if (config.blurSigma !== undefined) u.uBlurSigma.value = config.blurSigma;
        if (config.minThickness !== undefined) u.uMinThickness.value = config.minThickness;
        if (config.maxThickness !== undefined) u.uMaxThickness.value = config.maxThickness;
        if (config.criticalThickness !== undefined) u.uCriticalThickness.value = config.criticalThickness;
        if (config.highlightCritical !== undefined) u.uHighlightCritical.value = config.highlightCritical;
        if (config.criticalPulseSpeed !== undefined) u.uCriticalPulseSpeed.value = config.criticalPulseSpeed;
        if (config.criticalPulseIntensity !== undefined) u.uCriticalPulseIntensity.value = config.criticalPulseIntensity;
        if (config.sssEnabled !== undefined) u.uSSSEnabled.value = config.sssEnabled;
        if (config.sssIntensity !== undefined) u.uSSSIntensity.value = config.sssIntensity;
        if (config.sssColor !== undefined) u.uSSSColor.value = config.sssColor;
        if (config.aoIntensity !== undefined) u.uAOIntensity.value = config.aoIntensity;
        if (config.edgeGlow !== undefined) u.uEdgeGlow.value = config.edgeGlow;
        if (config.edgeGlowColor !== undefined) u.uEdgeGlowColor.value = config.edgeGlowColor;

        // Renk preset değişikliği
        if (config.colorPreset !== undefined) {
            const preset = ColorPresets[config.colorPreset] || ColorPresets.ct_scan;
            const colorThicknesses = new Float32Array(8);
            const colorValues: THREE.Color[] = [];

            for (let i = 0; i < 8; i++) {
                colorThicknesses[i] = preset.thicknesses[i] || 100;
                colorValues.push(preset.colors[i] || new THREE.Color(0x808080));
            }

            u.uColorThicknesses.value = colorThicknesses;
            u.uColorValues.value = colorValues;
        }
    }

    /**
     * Dinamik renk skalası ayarla (analiz sonuçlarına göre)
     */
    setDynamicColorScale(minThickness: number, maxThickness: number): void {
        const range = maxThickness - minThickness;
        const dynamicThicknesses = new Float32Array([
            minThickness,
            minThickness + range * 0.14,
            minThickness + range * 0.28,
            minThickness + range * 0.42,
            minThickness + range * 0.57,
            minThickness + range * 0.71,
            minThickness + range * 0.85,
            maxThickness
        ]);

        this.compositeMaterial.uniforms.uColorThicknesses.value = dynamicThicknesses;
        this.compositeMaterial.uniforms.uMinThickness.value = minThickness;
        this.compositeMaterial.uniforms.uMaxThickness.value = maxThickness;
    }

    /**
     * Animasyon frame güncelleme
     */
    update(deltaTime: number): void {
        this.time += deltaTime;
        this.compositeMaterial.uniforms.uTime.value = this.time;
    }

    /**
     * Multi-pass render
     * @param scene Scene
     * @param camera Camera
     * @param targetMesh Target mesh with thickness attributes
     * @param dataMaterial FikoThicknessDataMaterial for data pass
     * @param originalMaterial Original material to restore
     */
    render(
        scene: THREE.Scene,
        camera: THREE.Camera,
        targetMesh: THREE.Mesh,
        dataMaterial: FikoThicknessDataMaterial,
        originalMaterial: THREE.Material | THREE.Material[]
    ): void {
        const renderer = this.renderer;

        // Store state
        const currentRenderTarget = renderer.getRenderTarget();
        const currentClearColor = renderer.getClearColor(new THREE.Color());
        const currentClearAlpha = renderer.getClearAlpha();

        // ========== PASS 1: Thickness Data ==========
        targetMesh.material = dataMaterial;
        renderer.setRenderTarget(this.dataTarget);
        renderer.setClearColor(0x000000, 0);
        renderer.clear();
        renderer.render(scene, camera);

        // ========== PASS 2: Blur + Color Composite ==========
        renderer.setRenderTarget(currentRenderTarget);
        renderer.setClearColor(currentClearColor, currentClearAlpha);
        this.fsQuad.render(renderer);

        // Restore material
        targetMesh.material = originalMaterial;
    }

    /**
     * Kaynakları temizle
     */
    dispose(): void {
        this.dataTarget.dispose();
        this.compositeMaterial.dispose();
        this.fsQuad.dispose();
    }
}

/**
 * Helper: Mesh'e thickness ve SSS attribute'ları ekle
 */
export function setupThicknessMesh(
    geometry: THREE.BufferGeometry,
    thicknesses: Float32Array,
    sssFactors?: Float32Array
): void {
    const thicknessAttr = new THREE.BufferAttribute(thicknesses, 1);
    geometry.setAttribute('thickness', thicknessAttr);

    if (sssFactors) {
        const sssAttr = new THREE.BufferAttribute(sssFactors, 1);
        geometry.setAttribute('sssFactor', sssAttr);
    } else {
        // Varsayılan SSS: kalınlık azaldıkça SSS artar
        const defaultSSS = new Float32Array(thicknesses.length);
        let maxThickness = 0;
        for (let i = 0; i < thicknesses.length; i++) {
            if (thicknesses[i] > 0 && thicknesses[i] < 1000) {
                maxThickness = Math.max(maxThickness, thicknesses[i]);
            }
        }
        if (maxThickness > 0) {
            for (let i = 0; i < thicknesses.length; i++) {
                if (thicknesses[i] > 0 && thicknesses[i] < 1000) {
                    defaultSSS[i] = 1.0 - Math.min(thicknesses[i] / maxThickness, 1.0);
                } else {
                    defaultSSS[i] = 0;
                }
            }
        }
        const sssAttr = new THREE.BufferAttribute(defaultSSS, 1);
        geometry.setAttribute('sssFactor', sssAttr);
    }
}

// ============================================
// SMOOTH THICKNESS GEOMETRY - CT-SCAN KALİTESİ
// Flow analizi gibi pürüzsüz görünüm için:
// 1. Geometry Subdivision (her üçgen → 4 üçgen)
// 2. Laplacian Smoothing (komşu ortalaması)
// ============================================

/**
 * Thickness değerlerini smooth et (Laplacian smoothing)
 * Komşu vertex'lerin ortalamasını alarak üçgen kenarlarını yumuşatır
 *
 * @param geometry BufferGeometry
 * @param thicknesses Orijinal thickness değerleri
 * @param iterations Smoothing iterasyon sayısı (varsayılan: 5)
 * @param factor Smoothing faktörü 0-1 (varsayılan: 0.6)
 */
export function smoothThicknessValues(
    geometry: THREE.BufferGeometry,
    thicknesses: Float32Array,
    iterations: number = 5,
    factor: number = 0.6
): Float32Array {
    const vertexCount = thicknesses.length;
    const indices = geometry.index;

    if (!indices) {
        return thicknesses;
    }

    // Komşuluk haritası oluştur
    const neighbors: Map<number, Set<number>> = new Map();
    const indexArray = indices.array;

    for (let i = 0; i < indexArray.length; i += 3) {
        const a = indexArray[i];
        const b = indexArray[i + 1];
        const c = indexArray[i + 2];

        // Her vertex için komşularını kaydet
        if (!neighbors.has(a)) neighbors.set(a, new Set());
        if (!neighbors.has(b)) neighbors.set(b, new Set());
        if (!neighbors.has(c)) neighbors.set(c, new Set());

        neighbors.get(a)!.add(b);
        neighbors.get(a)!.add(c);
        neighbors.get(b)!.add(a);
        neighbors.get(b)!.add(c);
        neighbors.get(c)!.add(a);
        neighbors.get(c)!.add(b);
    }

    // Smoothing uygula
    let current = new Float32Array(thicknesses);
    let next = new Float32Array(vertexCount);

    for (let iter = 0; iter < iterations; iter++) {
        for (let v = 0; v < vertexCount; v++) {
            const neighborSet = neighbors.get(v);

            if (!neighborSet || neighborSet.size === 0) {
                next[v] = current[v];
                continue;
            }

            // Komşuların ortalamasını al
            let sum = 0;
            let validCount = 0;
            for (const n of neighborSet) {
                // Geçersiz değerleri atla
                if (current[n] > 0 && current[n] < 10000) {
                    sum += current[n];
                    validCount++;
                }
            }

            if (validCount > 0) {
                const avg = sum / validCount;
                // Mevcut değer ile ortalama arasında interpolasyon
                next[v] = current[v] * (1 - factor) + avg * factor;
            } else {
                next[v] = current[v];
            }
        }

        // Swap buffers
        const temp = current;
        current = next;
        next = temp;
    }

    return current;
}

/**
 * Mesh subdivision - her üçgeni 4 üçgene böler (Loop subdivision benzeri)
 * Daha fazla vertex oluşturur ve thickness geçişlerini yumuşatır
 *
 * @param geometry Orijinal geometry
 * @param thicknesses Orijinal thickness değerleri
 * @param sssFactors Orijinal SSS değerleri (opsiyonel)
 * @param levels Subdivision seviyesi (1 = 4x, 2 = 16x üçgen)
 * @returns Subdivide edilmiş geometry ve yeni attribute'lar
 */
export function subdivideForSmoothThickness(
    geometry: THREE.BufferGeometry,
    thicknesses: Float32Array,
    sssFactors?: Float32Array,
    levels: number = 1
): { geometry: THREE.BufferGeometry; thicknesses: Float32Array; sssFactors?: Float32Array } {

    let currentGeometry = geometry;
    let currentThicknesses = thicknesses;
    let currentSSSFactors = sssFactors;

    for (let level = 0; level < levels; level++) {
        const result = subdivideThicknessOnce(currentGeometry, currentThicknesses, currentSSSFactors);

        // Dispose intermediate geometry (not the original)
        if (level > 0 && currentGeometry !== geometry) {
            currentGeometry.dispose();
        }

        currentGeometry = result.geometry;
        currentThicknesses = result.thicknesses;
        currentSSSFactors = result.sssFactors;
    }

    return {
        geometry: currentGeometry,
        thicknesses: currentThicknesses,
        sssFactors: currentSSSFactors
    };
}

/**
 * Tek seviye subdivision - her üçgeni 4 üçgene böler
 */
function subdivideThicknessOnce(
    geometry: THREE.BufferGeometry,
    thicknesses: Float32Array,
    sssFactors?: Float32Array
): { geometry: THREE.BufferGeometry; thicknesses: Float32Array; sssFactors?: Float32Array } {

    const positions = geometry.getAttribute('position');
    const normals = geometry.getAttribute('normal');
    const indices = geometry.index;

    if (!indices) {
        return { geometry, thicknesses, sssFactors };
    }

    const indexArray = indices.array;
    const numTriangles = indexArray.length / 3;

    // Yeni vertex'ler için Map (edge midpoint cache)
    // Key: "minIdx_maxIdx", Value: yeni vertex index
    const edgeMidpoints: Map<string, number> = new Map();

    // Yeni veriler
    const newPositions: number[] = [];
    const newNormals: number[] = [];
    const newThicknesses: number[] = [];
    const newSSSFactors: number[] = [];
    const newIndices: number[] = [];

    // Önce orijinal vertex'leri kopyala
    for (let i = 0; i < positions.count; i++) {
        newPositions.push(positions.getX(i), positions.getY(i), positions.getZ(i));
        if (normals) {
            newNormals.push(normals.getX(i), normals.getY(i), normals.getZ(i));
        }
        newThicknesses.push(thicknesses[i]);
        if (sssFactors) {
            newSSSFactors.push(sssFactors[i]);
        }
    }

    let nextVertexIndex = positions.count;

    // Edge midpoint oluştur veya cache'den al
    function getOrCreateMidpoint(i0: number, i1: number): number {
        const minIdx = Math.min(i0, i1);
        const maxIdx = Math.max(i0, i1);
        const key = `${minIdx}_${maxIdx}`;

        if (edgeMidpoints.has(key)) {
            return edgeMidpoints.get(key)!;
        }

        // Yeni midpoint vertex oluştur
        const midIdx = nextVertexIndex++;
        edgeMidpoints.set(key, midIdx);

        // Position - ortala
        const x = (positions.getX(i0) + positions.getX(i1)) * 0.5;
        const y = (positions.getY(i0) + positions.getY(i1)) * 0.5;
        const z = (positions.getZ(i0) + positions.getZ(i1)) * 0.5;
        newPositions.push(x, y, z);

        // Normal - ortala ve normalize et
        if (normals) {
            let nx = (normals.getX(i0) + normals.getX(i1)) * 0.5;
            let ny = (normals.getY(i0) + normals.getY(i1)) * 0.5;
            let nz = (normals.getZ(i0) + normals.getZ(i1)) * 0.5;
            const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
            if (len > 0) {
                nx /= len;
                ny /= len;
                nz /= len;
            }
            newNormals.push(nx, ny, nz);
        }

        // Thickness - ortala
        const t0 = thicknesses[i0];
        const t1 = thicknesses[i1];
        // Geçersiz değerleri handle et
        let midThickness: number;
        if (t0 > 0 && t0 < 10000 && t1 > 0 && t1 < 10000) {
            midThickness = (t0 + t1) * 0.5;
        } else if (t0 > 0 && t0 < 10000) {
            midThickness = t0;
        } else if (t1 > 0 && t1 < 10000) {
            midThickness = t1;
        } else {
            midThickness = 0;
        }
        newThicknesses.push(midThickness);

        // SSS Factor - ortala
        if (sssFactors) {
            const sss = (sssFactors[i0] + sssFactors[i1]) * 0.5;
            newSSSFactors.push(sss);
        }

        return midIdx;
    }

    // Her üçgeni 4 üçgene böl
    for (let t = 0; t < numTriangles; t++) {
        const i0 = indexArray[t * 3];
        const i1 = indexArray[t * 3 + 1];
        const i2 = indexArray[t * 3 + 2];

        // 3 edge midpoint
        const m01 = getOrCreateMidpoint(i0, i1);
        const m12 = getOrCreateMidpoint(i1, i2);
        const m20 = getOrCreateMidpoint(i2, i0);

        // 4 yeni üçgen
        // Köşe üçgenleri
        newIndices.push(i0, m01, m20);   // Köşe 0
        newIndices.push(m01, i1, m12);   // Köşe 1
        newIndices.push(m20, m12, i2);   // Köşe 2
        // Merkez üçgen
        newIndices.push(m01, m12, m20);
    }

    // Yeni geometry oluştur
    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    newGeometry.setIndex(newIndices);

    if (newNormals.length > 0) {
        newGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
    } else {
        newGeometry.computeVertexNormals();
    }

    return {
        geometry: newGeometry,
        thicknesses: new Float32Array(newThicknesses),
        sssFactors: sssFactors ? new Float32Array(newSSSFactors) : undefined
    };
}

/**
 * CT-SCAN KALİTESİNDE SMOOTH THICKNESS GEOMETRY
 *
 * Flow analizi gibi pürüzsüz görünüm için tam pipeline:
 * 1. Geometry Subdivision (her üçgen → 4 üçgen) - daha fazla vertex
 * 2. Laplacian Smoothing (komşu ortalaması) - pürüzsüz geçişler
 * 3. Attribute'ları ayarla
 *
 * @param geometry BufferGeometry
 * @param thicknesses Her vertex için kalınlık değeri (mm)
 * @param sssFactors Opsiyonel SSS faktörleri
 * @param smoothIterations Smoothing iterasyon sayısı (varsayılan: 5)
 * @param subdivisionLevels Mesh subdivision seviyesi (varsayılan: 1)
 * @returns Yeni smooth geometry (orijinal modifiye edilmez)
 */
export function setupSmoothThicknessGeometry(
    geometry: THREE.BufferGeometry,
    thicknesses: Float32Array,
    sssFactors?: Float32Array,
    smoothIterations: number = 5,
    subdivisionLevels: number = 1
): THREE.BufferGeometry {

    let workGeometry = geometry;
    let workThicknesses = thicknesses;
    let workSSSFactors = sssFactors;

    // ADIM 1: Subdivision uygula (daha fazla vertex)
    if (subdivisionLevels > 0) {
        const subdivided = subdivideForSmoothThickness(geometry, thicknesses, sssFactors, subdivisionLevels);
        workGeometry = subdivided.geometry;
        workThicknesses = subdivided.thicknesses;
        workSSSFactors = subdivided.sssFactors;
    }

    // ADIM 2: Thickness değerlerini smooth et (Laplacian)
    const smoothedThicknesses = smoothThicknessValues(workGeometry, workThicknesses, smoothIterations, 0.6);

    // ADIM 3: Attribute'ları ayarla
    const thicknessAttr = new THREE.BufferAttribute(smoothedThicknesses, 1);
    workGeometry.setAttribute('thickness', thicknessAttr);

    // SSS faktörleri
    if (workSSSFactors) {
        // SSS değerlerini de smooth et
        const smoothedSSS = smoothThicknessValues(workGeometry, workSSSFactors, Math.max(2, smoothIterations - 2), 0.5);
        const sssAttr = new THREE.BufferAttribute(smoothedSSS, 1);
        workGeometry.setAttribute('sssFactor', sssAttr);
    } else {
        // Varsayılan SSS: kalınlık azaldıkça SSS artar
        const defaultSSS = new Float32Array(smoothedThicknesses.length);
        let maxThickness = 0;
        for (let i = 0; i < smoothedThicknesses.length; i++) {
            if (smoothedThicknesses[i] > 0 && smoothedThicknesses[i] < 10000) {
                maxThickness = Math.max(maxThickness, smoothedThicknesses[i]);
            }
        }
        if (maxThickness > 0) {
            for (let i = 0; i < smoothedThicknesses.length; i++) {
                if (smoothedThicknesses[i] > 0 && smoothedThicknesses[i] < 10000) {
                    defaultSSS[i] = 1.0 - Math.min(smoothedThicknesses[i] / maxThickness, 1.0);
                } else {
                    defaultSSS[i] = 0;
                }
            }
        }
        const sssAttr = new THREE.BufferAttribute(defaultSSS, 1);
        workGeometry.setAttribute('sssFactor', sssAttr);
    }

    return workGeometry;
}
